import React, { useEffect, useRef, useState } from 'react'
import { usePhoneCall } from '../hooks/usePhoneCall'
import { CallInfo } from './CallInfo'
import { CallBoostControl, CALL_BOOST_POPOVER_WIDTH_PX } from './CallBoostControl'
import { Dialer } from './Dialer'
import { MobileNumPad } from './MobileNumPad'
import { PhoneStateCard, type PhoneVisualState } from './PhoneStateCard'
import { SignalBars } from './SignalBars'
import { statusConfig } from '../config/statusConfig'
import type { Phone } from '../types'

interface Props {
  phoneId: string
  /** If true, hides the header block (name/SIM/status). Parent renders its own. */
  hideHeader?: boolean
  /**
   * Layout variant. `desktop` (default) — classic wide layout with hero card
   * above the dialer. `mobile` — full-height native-style composition used
   * inside the mobile Dialer tab. BOTH variants share the SAME `usePhoneCall`
   * instance: callers switch `variant` without changing `key`, so resize
   * between breakpoints never unmounts the call.
   */
  variant?: 'desktop' | 'mobile'
}

/**
 * Full dial/in-call UI for a single phone. Owns the call lifecycle via
 * usePhoneCall. Callers rendering this for different phones should pass
 * `key={phoneId}` to force remount between phones (see hook docstring).
 */
export function PhoneCallView({ phoneId, hideHeader = false, variant = 'desktop' }: Props) {
  const {
    phone,
    isLoading,
    error,
    isMuted,
    isCallPending,
    webrtc,
    quality,
    boostEnabled,
    boostGain,
    handleCall,
    handleHangup,
    handleAnswer,
    handleMute,
    handleSetBoostEnabled,
    handleSetBoostGain,
  } = usePhoneCall(phoneId)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-gray-400">Loading...</p>
      </div>
    )
  }

  if (error || !phone) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-gray-500">{error ? error.message : 'Phone not found'}</p>
      </div>
    )
  }

  const config = statusConfig[phone.status]
  const heroState = deriveHeroState(phone)

  if (variant === 'mobile') {
    return (
      <MobileLayout
        phone={phone}
        heroState={heroState}
        hideHeader={hideHeader}
        statusLabel={config.label}
        statusColor={config.color}
        statusDot={config.dot}
        isMuted={isMuted}
        isCallPending={isCallPending}
        webrtc={webrtc}
        quality={quality}
        boostEnabled={boostEnabled}
        boostGain={boostGain}
        onCall={handleCall}
        onHangup={handleHangup}
        onAnswer={handleAnswer}
        onMute={handleMute}
        onBoostEnabledChange={handleSetBoostEnabled}
        onBoostGainChange={handleSetBoostGain}
      />
    )
  }

  return (
    <div>
      {!hideHeader && (
        <header className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div className="max-w-3xl mx-auto px-4 md:px-16 py-5 flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                {phone.name}
              </h1>
              <p className="font-mono text-[13px] text-gray-500 dark:text-gray-400">
                {phone.sim}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${config.dot}`} />
              <span className={`font-mono text-xs font-medium ${config.color}`}>
                {config.label}
              </span>
            </div>
          </div>
        </header>
      )}

      {/* Hero state card — visual status + live signal bars during active call */}
      <div className="max-w-3xl mx-auto px-4 md:px-16 pt-6 flex justify-center">
        <PhoneStateCard
          state={heroState}
          size="lg"
          label={phone.name}
          quality={quality}
        />
      </div>

      {/* Always-visible WebRTC metrics — zeros when idle, live during call */}
      <div className="max-w-3xl mx-auto px-4 md:px-16 pt-3">
        <div className="text-[11px] font-mono text-gray-400 dark:text-gray-600 text-center tabular-nums">
          RTT: {webrtc.stats.rtt}ms | Jitter: {webrtc.stats.jitter}ms | Lost: {webrtc.stats.packetsLost}/{webrtc.stats.packetsReceived}
        </div>
      </div>

      {phone.currentCall && (
        <div className="max-w-3xl mx-auto px-4 md:px-16 pt-6">
          <CallInfo
            call={phone.currentCall}
            onMute={handleMute}
            onHangup={handleHangup}
            onAnswer={handleAnswer}
            isMuted={isMuted}
            audioState={webrtc.connectionState}
            boostEnabled={boostEnabled}
            boostGain={boostGain}
            onBoostEnabledChange={handleSetBoostEnabled}
            onBoostGainChange={handleSetBoostGain}
          />
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 md:px-16 py-8">
        <Dialer
          phoneStatus={phone.status}
          onCall={handleCall}
          disabled={isCallPending}
        />
      </div>
    </div>
  )
}

interface MobileLayoutProps {
  phone: Phone
  heroState: PhoneVisualState
  hideHeader: boolean
  statusLabel: string
  statusColor: string
  statusDot: string
  isMuted: boolean
  isCallPending: boolean
  webrtc: ReturnType<typeof usePhoneCall>['webrtc']
  quality: ReturnType<typeof usePhoneCall>['quality']
  boostEnabled: boolean
  boostGain: number
  onCall: (number: string) => void
  onHangup: () => void
  onAnswer: () => void
  onMute: () => void
  onBoostEnabledChange: (v: boolean) => void
  onBoostGainChange: (v: number) => void
}

function MobileLayout({
  phone,
  heroState,
  hideHeader,
  statusLabel,
  statusColor,
  statusDot,
  isMuted,
  isCallPending,
  webrtc,
  quality,
  boostEnabled,
  boostGain,
  onCall,
  onHangup,
  onAnswer,
  onMute,
  onBoostEnabledChange,
  onBoostGainChange,
}: MobileLayoutProps) {
  const call = phone.currentCall
  return (
    <div className="flex-1 flex flex-col min-h-0">
      {!hideHeader && (
        <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <div className="min-w-0">
            <h1 className="text-sm font-semibold truncate text-gray-900 dark:text-gray-100">
              {phone.name}
            </h1>
            <p className="font-mono text-[11px] truncate text-gray-500 dark:text-gray-400">
              {phone.sim}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 ml-3">
            <span className={`w-1.5 h-1.5 rounded-full ${statusDot}`} />
            <span className={`font-mono text-[10px] font-medium ${statusColor}`}>
              {statusLabel}
            </span>
          </div>
        </header>
      )}

      {call ? (
        <MobileActiveCall
          phone={phone}
          heroState={heroState}
          quality={quality}
          call={call}
          isMuted={isMuted}
          audioState={webrtc.connectionState}
          boostEnabled={boostEnabled}
          boostGain={boostGain}
          onHangup={onHangup}
          onAnswer={onAnswer}
          onMute={onMute}
          onBoostEnabledChange={onBoostEnabledChange}
          onBoostGainChange={onBoostGainChange}
        />
      ) : (
        <MobileDialer
          phone={phone}
          isCallPending={isCallPending}
          onCall={onCall}
        />
      )}
    </div>
  )
}

function MobileDialer({
  phone,
  isCallPending,
  onCall,
}: {
  phone: Phone
  isCallPending: boolean
  onCall: (number: string) => void
}) {
  return (
    <div className="flex-1 flex flex-col overflow-y-auto">
      <MobileNumPad
        phoneStatus={phone.status}
        onCall={onCall}
        disabled={isCallPending}
      />
    </div>
  )
}

function MobileActiveCall({
  phone,
  heroState,
  quality,
  call,
  isMuted,
  audioState,
  boostEnabled,
  boostGain,
  onHangup,
  onAnswer,
  onMute,
  onBoostEnabledChange,
  onBoostGainChange,
}: {
  phone: Phone
  heroState: PhoneVisualState
  quality: ReturnType<typeof usePhoneCall>['quality']
  call: NonNullable<Phone['currentCall']>
  isMuted: boolean
  audioState: RTCPeerConnectionState | null
  boostEnabled: boolean
  boostGain: number
  onHangup: () => void
  onAnswer: () => void
  onMute: () => void
  onBoostEnabledChange: (v: boolean) => void
  onBoostGainChange: (v: number) => void
}) {
  const isRinging = call.state === 'ringing'
  const isDialing = call.state === 'dialing'
  const isActive = call.state === 'active'
  const stateLabel = isRinging
    ? 'Входящий звонок'
    : isDialing
      ? 'Исходящий'
      : 'В разговоре'

  // Mobile popover position: ABOVE the trigger button, not below.
  // The buttons live near the bottom of the viewport — placing the
  // popover below would clip below safe-area. We anchor by `bottom`
  // (distance from viewport bottom) and center horizontally.
  const boostBtnRef = useRef<HTMLButtonElement>(null)
  const [boostPos, setBoostPos] = useState<{ bottom: number; left: number } | null>(null)
  const boostOpen = boostPos !== null
  const toggleBoost = (): void => {
    if (boostOpen) {
      setBoostPos(null)
      return
    }
    const rect = boostBtnRef.current?.getBoundingClientRect()
    if (!rect) return
    // Place 8px above the button. Center horizontally, but keep within
    // viewport with 16px margin on each side.
    const margin = 16
    const idealLeft = rect.left + rect.width / 2 - CALL_BOOST_POPOVER_WIDTH_PX / 2
    const maxLeft = window.innerWidth - CALL_BOOST_POPOVER_WIDTH_PX - margin
    const left = Math.max(margin, Math.min(maxLeft, idealLeft))
    setBoostPos({ bottom: window.innerHeight - rect.top + 8, left })
  }
  const closeBoost = (): void => setBoostPos(null)

  return (
    <div className="flex-1 flex flex-col items-center justify-between px-6 py-8">
      <div className="flex flex-col items-center">
        <p className="font-mono text-[11px] tracking-wider text-gray-500 uppercase">
          {stateLabel}
        </p>
        <h2 className="mt-3 font-mono text-3xl font-semibold text-gray-900 dark:text-gray-100">
          {call.number}
        </h2>
        {isActive && <CallDuration startedAt={call.startedAt} />}
      </div>

      <PhoneStateCard state={heroState} size="lg" label={phone.name} quality={quality} />

      <div className="w-full flex flex-col items-center gap-3">
        {/* Signal + audio status — only meaningful once the call is actually
            connected. During ringing/dialing the PC isn't established yet, so
            `audioState` is null and `quality` falls to the optimistic default;
            showing "Связь стабильна" there would mislead the operator. */}
        {isActive && quality !== undefined && (
          <div className="flex items-center gap-2 text-[11px] font-mono text-gray-500">
            <SignalBars level={quality} size="md" />
            {audioState && audioState !== 'connected' && (
              <span>
                {audioState === 'failed'
                  ? 'Ошибка соединения'
                  : audioState === 'disconnected'
                    ? 'Соединение потеряно'
                    : 'Подключение аудио...'}
              </span>
            )}
          </div>
        )}

        {isRinging ? (
          <div className="flex gap-4 w-full justify-center">
            <RoundButton label="Принять" variant="green" onClick={onAnswer}>
              <AnswerIcon />
            </RoundButton>
            <RoundButton label="Отклонить" variant="red" onClick={onHangup}>
              <HangupIcon />
            </RoundButton>
          </div>
        ) : (
          <div className="flex gap-4 w-full justify-center">
            <RoundButton
              label={boostEnabled ? `Усиление ${Math.round(boostGain * 100)}%` : 'Усиление выключено'}
              variant={boostEnabled ? 'green-outline' : 'neutral'}
              onClick={toggleBoost}
              ref={boostBtnRef}
            >
              <BoostIcon active={boostEnabled} />
            </RoundButton>
            <RoundButton
              label={isMuted ? 'Включить звук' : 'Выключить звук'}
              variant={isMuted ? 'red-outline' : 'neutral'}
              onClick={onMute}
            >
              <MuteIcon muted={isMuted} />
            </RoundButton>
            <RoundButton label="Сбросить" variant="red" onClick={onHangup}>
              <HangupIcon />
            </RoundButton>
          </div>
        )}
      </div>

      {boostOpen && boostPos && (
        <CallBoostControl
          enabled={boostEnabled}
          gain={boostGain}
          onEnabledChange={onBoostEnabledChange}
          onGainChange={onBoostGainChange}
          position={boostPos}
          onClose={closeBoost}
          triggerRef={boostBtnRef}
          controlsTestId="mobile-call-boost"
        />
      )}
    </div>
  )
}

function CallDuration({ startedAt }: { startedAt: string }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const seconds = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000))
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return (
    <p className="mt-2 font-mono text-xl font-semibold text-gray-700 dark:text-gray-300 tabular-nums">
      {`${m}:${s.toString().padStart(2, '0')}`}
    </p>
  )
}

type RoundButtonVariant = 'green' | 'red' | 'red-outline' | 'green-outline' | 'neutral'

const ROUND_BUTTON_VARIANTS: Record<RoundButtonVariant, string> = {
  green: 'bg-green-600 hover:bg-green-700 text-white',
  red: 'bg-red-600 hover:bg-red-700 text-white',
  'red-outline': 'bg-red-50 dark:bg-red-950/30 text-red-600 border border-red-500',
  'green-outline': 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 border border-emerald-500',
  neutral: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700',
}

type RoundButtonProps = {
  label: string
  variant: RoundButtonVariant
  onClick: () => void
  children: React.ReactNode
}

const RoundButton = React.forwardRef<HTMLButtonElement, RoundButtonProps>(function RoundButton(
  { label, variant, onClick, children },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${ROUND_BUTTON_VARIANTS[variant]}`}
    >
      {children}
    </button>
  )
})

function AnswerIcon() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  )
}

function HangupIcon() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
      <line x1="23" y1="1" x2="1" y2="23" />
    </svg>
  )
}

function MuteIcon({ muted }: { muted: boolean }) {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {muted ? (
        <>
          <line x1="1" y1="1" x2="23" y2="23" />
          <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
          <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .7-.1 1.38-.3 2.02" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </>
      ) : (
        <>
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </>
      )}
    </svg>
  )
}

function BoostIcon({ active }: { active: boolean }) {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      {active && <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />}
    </svg>
  )
}

/**
 * Map the live Phone model to a PhoneStateCard visual state. `dialing` has
 * no dedicated visual — it shares the `in-call` treatment so the operator
 * sees activity instead of a flat idle state while the call is being
 * established. See plan "dialing compromise".
 */
function deriveHeroState(phone: Phone): PhoneVisualState {
  if (phone.status === 'offline') return 'offline'
  if (phone.currentCall?.state === 'ringing') return 'ringing'
  if (phone.currentCall || phone.status === 'in-call') return 'in-call'
  return 'normal'
}
